/**
 * examLogic.js (QUIZ ONLY)
 * Prepares payload + strict instructions for QUIZ GENERATION only.
 * ❌ Summary is FORCED DISABLED.
 * ❌ Flashcards are FORCED DISABLED.
 * ✅ Math-content detection + "Solving MCQ" enforcement included.
 * ✅ Batch payload generation supported.
 * ✅ NEW: "Anti-Hallucination" Hard Mode prompts.
 */

const ALLOWED_QUIZ_TYPES = new Set([
  "true-false",
  "identification",
  "fill-blanks",
  "mcq",
  "mixed",
]);

const ALLOWED_DIFFICULTIES = new Set(["easy", "medium", "hard"]);

// ✅ UPDATED: Max limit increased to 100 to match your UI
const clamp = (n, min, max) => Math.max(min, Math.min(max, n));

const normalizeWhitespace = (s) =>
  String(s || "")
    .replace(/\r\n/g, "\n")
    .replace(/[ \t]+/g, " ")
    .replace(/\n{3,}/g, "\n\n")
    .trim();

const sanitizeUserNotes = (s) => {
  return normalizeWhitespace(s)
    .replace(/```[\s\S]*?```/g, "")
    .replace(/<script[\s\S]*?>[\s\S]*?<\/script>/gi, "")
    .slice(0, 2000);
};

const normalizeDifficulty = (d) => {
  if (d === null || d === undefined) return "medium";

  if (typeof d === "object") {
    return normalizeDifficulty(d.value || d.id || d.difficulty || "medium");
  }

  if (typeof d === "number" && Number.isFinite(d)) {
    if (d === 1) return "easy";
    if (d === 2) return "medium";
    if (d >= 3) return "hard"; 
  }

  if (typeof d === "string") {
    const v = d.trim().toLowerCase();
    if (v === "1" || v === "easy") return "easy";
    if (v === "2" || v === "medium") return "medium";
    if (v === "3" || v === "hard") return "hard";
    if (ALLOWED_DIFFICULTIES.has(v)) return v;
  }
  
  return "medium";
};

// ======================================================================
// ✅ DYNAMIC SYSTEM PROMPTS (FIXED TO PREVENT HALLUCINATIONS)
// ======================================================================
const getSystemPrompt = (difficulty) => {
  const isHard = difficulty === "hard";

  // 🔴 HARD MODE: Refined to stop "Nurse in Airport" errors
  if (isHard) {
    return `STRICT SYSTEM INSTRUCTION: You are an Exam Generator API. Return valid JSON only.

Your output MUST be a single JSON object:
{ "summary": "", "flashcards": [], "quiz": [{ "question": "...", "options": [...], "answer": "...", "explanation": "..." }] }

HARD RULES (JSON):
1) Output MUST be parseable JSON.
2) "summary" MUST be empty.
3) "flashcards" MUST be empty.

CONTENT RULES (HARD MODE ACTIVE):
1) **CONCEPT APPLICATION:** Focus on the *application* of concepts, not simple recall.
2) **SCENARIO LOGIC:** You may use hypothetical scenarios, but they must be LOGICALLY CONSISTENT. 
   - CRITICAL: Do not mix terminology from different domains. (e.g., If you change a setting to an "Airport", use "Security Officer" instead of "Nurse").
   - Do not create absurd situations just to be "different".
3) **DEEP THINKING:** Questions should require identifying root causes, predicting outcomes, or evaluating trade-offs.
4) **10/10 DIFFICULTY TARGET:** Prioritize multi-step reasoning and ambiguous-realistic cases over direct recall.
5) **CASE EMPHASIS:** At least 70% of items should be scenario-driven.
6) **NO CLUE PATTERNS:** Do not reveal answers via length, grammar mismatch, obvious absolutes, or repeated wording from source.
`;
  }

  // 🟢 NORMAL/EASY MODE: Strict grounding
  return `STRICT SYSTEM INSTRUCTION: You are an Exam Generator API. Return valid JSON only.

Your output MUST be a single JSON object:
{ "summary": "", "flashcards": [], "quiz": [{ "question": "...", "options": [...], "answer": "...", "explanation": "..." }] }

HARD RULES (JSON):
1) Output MUST be parseable JSON.
2) "summary" MUST be empty.
3) "flashcards" MUST be empty.

CONTENT RULES:
1) Generate questions STRICTLY from the provided text.
2) Use the exact examples found in the source.
3) Do not hallucinate.
4) For MCQ, never make the correct choice obviously longer or more specific than distractors.
`;
};

// ======================================================================
// ✅ MATH/CALCULATION HEURISTIC
// ======================================================================
const detectMathyContent = (text) => {
  const s = String(text || "");
  if (!s.trim()) return false;

  let score = 0;
  if (/[\\$][\s\S]*?(frac|sqrt|cdot|times|left|right|sum|int|lim)/i.test(s)) score += 3;
  if (/<sup|<sub/i.test(s)) score += 2;
  if (/[=]/.test(s)) score += 1;
  if (/[+\-*/^]/.test(s)) score += 1;
  if (/\b(equation|solve|simplify|derive|derivative|integral|limit|logarithm|probability|statistics|matrix|vector|scalar)\b/i.test(s)) score += 2;
  if (/\b(velocity|acceleration|force|energy|power|work|momentum|mass|density|pressure|voltage|current|resistance|ohm|newton|joule|watt)\b/i.test(s)) score += 2;
  if (/\b\d+(\.\d+)?\b/.test(s)) score += 1;
  
  return score >= 4;
};

const QUIZ_DEFINITIONS = {
  "true-false": `TYPE: True/False\n- "options" MUST always be ["True", "False"].`,
  "identification": `TYPE: Identification\n- Question is the definition.\n- Answer is the term.`,
  "fill-blanks": `TYPE: Fill-in-the-Blank\n- Question must contain "_______".`,
  "mcq": `TYPE: Multiple Choice (MCQ)\n- "options" MUST contain 4 distinct choices.\n- "answer" MUST be the full text of the correct option.\n- STRICT OPTION LENGTH SYMMETRY: All 4 choices MUST have approximately the same character count and word count (target +/-10%).\n- The correct option MUST NOT be the uniquely longest or most detailed choice.\n- If the correct option is longer/detailed, strengthen and expand all 3 distractors to match its length and specificity before final output.`,
};

// ======================================================================
// ✅ CHOICE BALANCING RULES
// ======================================================================
const getHardModeOptionRules = (difficulty) => {
  const baseRules = `
### MCQ CHOICE BALANCE RULES (MANDATORY) ###
1. **UNIFORM LENGTH:** Ensure all 4 MCQ options are roughly the same character length and word count to prevent guessing based on length.
2. **NO OBVIOUS ANSWERS:** Do NOT make the correct choice longer or more detailed than the distractors.
3. **SELF-CHECK BEFORE OUTPUT:** Inspect all 4 choices. If the correct choice is longer, expand the other 3 choices to balance them evenly.
`;

  if (difficulty !== "hard") return baseRules;

  return `
${baseRules}
### CRITICAL OVERRIDE: HARD MODE ACTIVE ###
1. **NO LAZY COPYING:** Do not just copy-paste sentences from the text. Rephrase concepts to test understanding.
2. **PLAUSIBLE DISTRACTORS:** Wrong answers must be technically plausible concepts from the same domain, not obvious errors.
3. **AVOID "ALL OF THE ABOVE":** Do not use "All of the above" or "None of the above" unless absolutely necessary.
4. **NEAR-MISS DISTRACTORS:** Make distractors close alternatives that fail on subtle constraints.
5. **ANSWER BALANCE:** Spread correct options across A/B/C/D without a detectable pattern.
6. **EXPLANATION RIGOR:** Explain why the correct option is best and why at least one strong distractor is wrong.
`;
};

// ======================================================================
// ✅ MATH "SOLVING MCQ" RULES
// ======================================================================
const getMathSolvingRules = (solvingCount, difficulty) => {
  const sc = Math.max(1, solvingCount || 1);
  const hardModeConstraint = difficulty === "hard" 
    ? "- Ensure all 4 calculated options are formatted with the SAME number of decimal places or sig-figs." 
    : "";

  return `MATH SOLVING MCQ MODE:
- At least ${sc} of the quiz items MUST be SOLVING problems.
- "explanation" MUST show step-by-step solving.
${hardModeConstraint}`;
};

const getQuizInstructions = (quizTypesInput, totalCount, isMathContent, difficulty) => {
  // ✅ UPDATED: Clamp to 100 max
  const qc = Number.isFinite(totalCount) ? clamp(totalCount, 1, 100) : 10;

  if (qc <= 0) return ""; 

  let selectedTypes = [];
  if (Array.isArray(quizTypesInput)) {
    selectedTypes = quizTypesInput;
  } else if (typeof quizTypesInput === "string") {
    if (quizTypesInput === "mixed") {
      selectedTypes = ["mcq", "identification", "fill-blanks", "true-false"];
    } else {
      selectedTypes = [quizTypesInput];
    }
  }

  selectedTypes = selectedTypes.filter((t) => QUIZ_DEFINITIONS[t]);
  if (selectedTypes.length === 0) selectedTypes = ["mcq"];

  const typeCount = selectedTypes.length;
  const baseCount = Math.floor(qc / typeCount);
  let remainder = qc % typeCount;

  let distributionString = "";
  let rulesString = "";

  selectedTypes.forEach((type) => {
    const countForThisType = baseCount + (remainder > 0 ? 1 : 0);
    remainder--;

    if (countForThisType > 0) {
      distributionString += `- ${countForThisType} ${type.toUpperCase()} questions\n`;
      rulesString += `\n[RULES FOR ${type.toUpperCase()}]:\n${QUIZ_DEFINITIONS[type]}\n`;
    }
  });

  const solvingCount = isMathContent ? clamp(Math.round(qc * 0.6), 1, qc) : 0;
  const mathSolvingBlock = isMathContent ? `\n${getMathSolvingRules(solvingCount, difficulty)}\n` : "";

  return `QUIZ GENERATION RULES:
You MUST generate EXACTLY ${qc} quiz items total.
The quiz types must be distributed as follows:
${distributionString}

${difficulty === "hard" ? `HARD MODE QUESTION DESIGN:
- Use "most defensible", "best next action", and trade-off prompts.
- Avoid pure textbook-definition recall unless required by question type.
- Require interpretation of context constraints, not surface keyword matching.` : ""}

SPECIFIC FORMATTING RULES PER TYPE:
${rulesString}

${mathSolvingBlock}

IMPORTANT:
- Include "explanation" key for EVERY item.`;
};

// ======================================================================
// ✅ BATCHING HELPERS
// ======================================================================
const splitIntoParagraphs = (text) => {
  const t = normalizeWhitespace(text);
  return t.split(/\n{2,}/g).map((p) => p.trim()).filter(Boolean);
};

const splitHugeParagraph = (p, hardMaxChars) => {
  if (p.length <= hardMaxChars) return [p];
  const parts = p.split(/(?<=[.!?])\s+/g);
  // Fallback for unpunctuated/corrupted text that cannot be sentence-split.
  if (parts.length <= 1) {
    const out = [];
    for (let i = 0; i < p.length; i += hardMaxChars) {
      const chunk = p.slice(i, i + hardMaxChars).trim();
      if (chunk) out.push(chunk);
    }
    return out;
  }

  const out = [];
  let buf = "";
  for (const s of parts) {
    if (s.length > hardMaxChars) {
      if (buf.trim()) {
        out.push(buf.trim());
        buf = "";
      }
      for (let i = 0; i < s.length; i += hardMaxChars) {
        const chunk = s.slice(i, i + hardMaxChars).trim();
        if (chunk) out.push(chunk);
      }
      continue;
    }

    if ((buf + " " + s).trim().length > hardMaxChars) {
      if (buf.trim()) out.push(buf.trim());
      buf = s;
    } else {
      buf = (buf + " " + s).trim();
    }
  }
  if (buf.trim()) out.push(buf.trim());
  return out;
};

const splitContentIntoBatches = (rawText, opts = {}) => {
  const { maxCharsPerBatch = 35000, hardMaxChars = 50000, overlapChars = 500 } = opts;
  const paragraphs = splitIntoParagraphs(rawText).flatMap((p) => splitHugeParagraph(p, hardMaxChars));
  const batches = [];
  let cur = "";
  const splitByMax = (text, maxChars) => {
    const out = [];
    for (let i = 0; i < text.length; i += maxChars) {
      const chunk = text.slice(i, i + maxChars).trim();
      if (chunk) out.push(chunk);
    }
    return out;
  };

  for (const originalP of paragraphs) {
    const parts = originalP.length > maxCharsPerBatch
      ? splitByMax(originalP, maxCharsPerBatch)
      : [originalP];

    for (const p of parts) {
    const next = (cur ? cur + "\n\n" : "") + p;
    if (next.length <= maxCharsPerBatch) {
      cur = next;
      continue;
    }
    if (cur.trim()) batches.push(cur.trim());
    if (overlapChars > 0 && batches.length > 0) {
      const prev = batches[batches.length - 1];
      const overlap = prev.slice(Math.max(0, prev.length - overlapChars));
      cur = (overlap.trim() ? overlap.trim() + "\n\n" : "") + p;
    } else {
      cur = p;
    }
    }
  }
  if (cur.trim()) batches.push(cur.trim());
  return batches;
};

const distributeTotalAllowZero = (total, weights) => {
  const T = Math.max(0, Math.floor(Number(total) || 0));
  const n = weights.length;
  if (n === 0) return [];
  if (T === 0) return Array(n).fill(0);

  const w = weights.map((x) => Math.max(1, Number(x) || 1));
  const sumW = w.reduce((a, b) => a + b, 0);
  const raw = w.map((x) => (x / sumW) * T);
  const base = raw.map((x) => Math.floor(x));
  let remainder = T - base.reduce((a, b) => a + b, 0);

  const frac = raw.map((x, i) => ({ i, f: x - base[i] })).sort((a, b) => b.f - a.f);
  for (let k = 0; k < frac.length && remainder > 0; k++) {
    base[frac[k].i] += 1;
    remainder -= 1;
  }
  return base;
};

const capAndKeepTotal = (counts, maxPerBatch) => {
  let overflow = 0;
  const capped = counts.map((c) => {
    if (c > maxPerBatch) {
      overflow += (c - maxPerBatch);
      return maxPerBatch;
    }
    return c;
  });
  if (overflow === 0) return capped;

  let guard = 0;
  while (overflow > 0 && guard < 100000) {
    guard++;
    let moved = false;
    for (let i = 0; i < capped.length && overflow > 0; i++) {
      if (capped[i] < maxPerBatch) {
        capped[i] += 1;
        overflow -= 1;
        moved = true;
      }
    }
    if (!moved) break;
  }
  return capped;
};

// ======================================================================
// ✅ MAIN: SINGLE PAYLOAD (QUIZ ONLY)
// ======================================================================
export const prepareReviewerPayload = (rawContent, config, batchMeta = null) => {
  const {
    difficulty: rawDifficulty = "medium",
    quizType = ["mcq"],
    questionCount = 10,
    timePerQuestion = 30,
    customInstructions = "",
    eli5 = false,
    quizEnabled = true, 
  } = config || {};

  if (typeof rawContent !== "string") throw new Error("No text content found.");
  const content = normalizeWhitespace(rawContent);
  if (content.length < 50) throw new Error("Content too short.");

  const summaryEnabled = false;
  const flashcardsEnabled = false;
  
  const difficulty = normalizeDifficulty(rawDifficulty);
  const isBatchMode = !!batchMeta && Number(batchMeta?.batchCount || 0) > 1;
  
  // ✅ UPDATED: Clamp to 100 max
  let qc = clamp(Number(questionCount) || 10, 1, 100);
  if (quizEnabled === false) {
    qc = 0;
  }

  const isMathContent = detectMathyContent(content);
  const systemPrompt = getSystemPrompt(difficulty);
  const specificQuizRule = qc > 0 ? getQuizInstructions(quizType, qc, isMathContent, difficulty) : "";
  const hardModeBlock = getHardModeOptionRules(difficulty);
  const userNotes = sanitizeUserNotes(customInstructions);

  const carryOverRule = isBatchMode
    ? `BATCH MODE: Processing Batch ${batchMeta.batchIndex + 1} of ${batchMeta.batchCount}. Focus ONLY on this content.`
    : "";

  const featureRules = `
FEATURE RULES:
- summary: "" (Always empty)
- flashcards: [] (Always empty)
- quiz: ${qc > 0 ? `Generate EXACTLY ${qc} items.` : "[] (Empty)"}
`.trim();

  const readabilityRule = eli5 ? `READABILITY: Use simple words (ELI5).` : `READABILITY: Standard academic tone.`;

  const finalInstructions = `
${systemPrompt}

${featureRules}

${specificQuizRule}

${carryOverRule}

${readabilityRule}

USER PREFERENCES:
${userNotes || "None"}

${hardModeBlock}
`.trim();

  return {
    content,
    difficulty,
    eli5,
    summaryEnabled,
    flashcardsEnabled,
    quizEnabled: qc > 0, 
    questionCount: qc,
    flashcardCount: 0,
    timePerQuestion,
    quizType,
    customInstructions: finalInstructions,
    userInstruction: userNotes || "",
    isMathContent,
    batchMeta: batchMeta || null,
  };
};

// ======================================================================
// ✅ BATCH PAYLOADS (QUIZ ONLY)
// ======================================================================
export const prepareReviewerBatchPayloads = (rawContent, config, batchOptions = {}) => {
  const {
    maxCharsPerBatch = 35000,
    hardMaxChars = 50000,
    overlapChars = 500,
    totalQuestionsMode = true,
    // ✅ UPDATED: Default max questions per batch to 100
    maxQuestionsPerBatch = 100,
    difficulty: batchDifficultyOverride,
  } = batchOptions;

  if (typeof rawContent !== "string") throw new Error("No text content found.");
  const normalized = normalizeWhitespace(rawContent);

  const batches = splitContentIntoBatches(normalized, { maxCharsPerBatch, hardMaxChars, overlapChars });
  const batchCount = batches.length;
  if (batchCount === 0) throw new Error("No batches produced.");

  const weights = batches.map((b) => b.length);

  let perBatchQuiz = Array(batchCount).fill(0);
  const requestedTotalQuiz = Math.max(0, Math.floor(Number(config.questionCount ?? 10)));

  if (totalQuestionsMode) {
    perBatchQuiz = distributeTotalAllowZero(requestedTotalQuiz, weights);
    // ✅ UPDATED: Ensure batch capping supports 100
    perBatchQuiz = capAndKeepTotal(perBatchQuiz, Number(maxQuestionsPerBatch || 100));
  } else {
    // ✅ UPDATED: Clamp to 100
    const per = clamp(Number(config.questionCount) || 10, 1, 100);
    perBatchQuiz = Array(batchCount).fill(per);
  }

  const payloads = batches.map((batchText, i) => {
    const qCount = perBatchQuiz[i] || 0;

    const cfg = {
      ...(config || {}),
      difficulty: config?.difficulty || batchDifficultyOverride || "medium", 
      quizEnabled: qCount > 0, 
      questionCount: qCount,
      summaryEnabled: false,
      flashcardsEnabled: false,
      flashcardCount: 0,
    };

    return prepareReviewerPayload(batchText, cfg, {
      batchIndex: i,
      batchCount,
      approxChars: batchText.length,
    });
  });

  return { batchCount, payloads };
};

export const safeParseAIJson = (rawString) => {
  if (!rawString) return { summary: "", flashcards: [], quiz: [] };
  try {
    const clean = rawString
      .replace(/```json/g, "")
      .replace(/```/g, "")
      .trim();
    return JSON.parse(clean);
  } catch (e) {
    console.error("JSON Parse Error:", e);
    return { summary: "", flashcards: [], quiz: [] };
  }
};


